import { computed, markRaw, reactive, ref, toValue } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { menuRoutes, menuRouteEnum } from '@/router'
import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore, elementLayoutTypeEnum } from '@/stores/web-site.store'
import { useCurrency } from '@/composables/currency'
import { useNumberComparison } from '@/composables/number-comparison'
import { useRateCard } from '@/composables/rate-card'
import { useNotify } from '@/composables/notify'
import { signalToneEnum, signalToneColor } from '@/composables/dashboard-kpi'
import { formatShortDate, getInitials, isNotEmpty } from '@/lib/utils'

export const ROI_COMPARISON_LIMITS = Object.freeze({ MIN: 2, MAX: 10 })
export const ROI_COMPARISON_NAME_MAX_LENGTH = 200
export const ROI_COMPARISON_COLORS = Object.freeze({ ACCENT: 'purple-1', BEST: 'positive' })

export const resolveReferenceId = (items, referenceId) => (items.some(item => item.id === referenceId) ? referenceId : items[0]?.id ?? null)

export const useRoiComparisonLimits = (items) => {
  const isFull = computed(() => toValue(items).length >= ROI_COMPARISON_LIMITS.MAX)
  const isComparable = computed(() => toValue(items).length >= ROI_COMPARISON_LIMITS.MIN)
  const limitColor = computed(() => signalToneColor(isFull.value ? signalToneEnum.ATTENTION : signalToneEnum.NEUTRAL))
  const limitLabel = computed(() => `${toValue(items).length} / ${ROI_COMPARISON_LIMITS.MAX}`)
  return { isFull, isComparable, limitColor, limitLabel }
}

let definitionsCache = null
const numberFormatCache = new Map()

const useRoiComparisonDefinitions = () => {
  if (definitionsCache) return definitionsCache
  const planningStore = usePlanningStore()
  const { roiComparisonTypeEnum: TYPE, roiMetricFormatEnum: FORMAT, roiMetricBetterEnum: BETTER } = planningStore
  const { priceLabel, costLabel } = useRateCard()

  const metric = (key, labelKey, format, better = BETTER.NONE, options = {}) => ({ key, labelKey, format, better, ...options })

  const definitions = {
    [TYPE.EQUIPMENT]: {
      labelKey: 'common.equipment',
      icon: 'mdi-camcorder',
      menuRoute: menuRouteEnum.EQUIPMENTS,
      groups: [
        { labelKey: 'roiCompare.group.purchase', metrics: [
          metric('purchase_date', 'common.buyDate', FORMAT.DATE),
          metric('purchase_cost', 'common.purchaseCost', FORMAT.MONEY),
        ] },
        { labelKey: 'common.rateCard', metrics: [
          metric('rc_price', 'common.price', FORMAT.MONEY, BETTER.NONE, { display: priceLabel }),
          metric('rc_cost', 'common.cost', FORMAT.MONEY, BETTER.NONE, { display: costLabel }),
        ] },
        { labelKey: 'roiCompare.group.usage', metrics: [
          metric('project_count', 'common.project', FORMAT.INTEGER, BETTER.HIGHER, { labelCount: 2 }),
          metric('completed_project_count', 'company.roi.completedProjects', FORMAT.INTEGER, BETTER.HIGHER),
          metric('critical_status_changes', 'common.criticalConditions', FORMAT.INTEGER, BETTER.LOWER),
        ] },
        { labelKey: 'roiCompare.group.economics', metrics: [
          metric('revenue', 'common.revenue', FORMAT.MONEY, BETTER.HIGHER, { labelCount: 2 }),
          metric('completed_revenue', 'roiCompare.completedRevenue', FORMAT.MONEY, BETTER.HIGHER),
          metric('margin', 'common.margin', FORMAT.MONEY, BETTER.HIGHER, { signed: true }),
        ] },
        { labelKey: 'roiCompare.group.performance', metrics: [
          metric('roi', 'common.roi', FORMAT.PERCENT, BETTER.HIGHER, { signed: true }),
        ] },
      ],
    },
    [TYPE.COMPANY]: {
      labelKey: 'common.company',
      icon: 'mdi-office-building-outline',
      menuRoute: menuRouteEnum.COMPANIES,
      groups: [
        { labelKey: 'roiCompare.group.billing', metrics: [
          metric('invoices_total_year', 'company.roi.invoicesTotalYear', FORMAT.MONEY, BETTER.HIGHER),
          metric('quotes_total_year', 'company.roi.quotesTotalYear', FORMAT.MONEY, BETTER.HIGHER),
        ] },
        { labelKey: 'common.margin', labelCount: 2, metrics: [
          metric('gross_margin_year', 'company.roi.grossMarginYear', FORMAT.MONEY, BETTER.HIGHER, { signed: true }),
          metric('gross_margin_quarter', 'company.roi.grossMarginQuarter', FORMAT.MONEY, BETTER.HIGHER, { signed: true }),
          metric('avg_equipment_margin', 'company.roi.avgEquipmentMargin', FORMAT.MONEY, BETTER.HIGHER, { signed: true }),
          metric('avg_staff_margin', 'company.roi.avgStaffMargin', FORMAT.MONEY, BETTER.HIGHER, { signed: true }),
        ] },
        { labelKey: 'common.project', labelCount: 2, metrics: [
          metric('total_projects', 'roiCompare.totalProjects', FORMAT.INTEGER, BETTER.HIGHER),
          metric('current_year_projects', 'roiCompare.currentYearProjects', FORMAT.INTEGER, BETTER.HIGHER),
          metric('active_projects', 'company.roi.activeProjects', FORMAT.INTEGER, BETTER.HIGHER),
          metric('completed_projects', 'company.roi.completedProjects', FORMAT.INTEGER, BETTER.HIGHER),
          metric('first_project_date', 'roiCompare.firstProject', FORMAT.DATE),
        ] },
        { labelKey: 'roiCompare.group.composition', metrics: [
          metric('avg_equipment_qty_per_project', 'company.roi.avgEquipmentQty', FORMAT.NUMBER),
          metric('avg_staff_qty_per_project', 'company.roi.avgStaffQty', FORMAT.NUMBER),
        ] },
      ],
    },
    [TYPE.STAFF]: {
      labelKey: 'common.staff',
      icon: 'mdi-account',
      menuRoute: menuRouteEnum.STAFFS,
      groups: [
        { labelKey: 'roiCompare.group.usage', metrics: [
          metric('project_count', 'common.project', FORMAT.INTEGER, BETTER.HIGHER, { labelCount: 2 }),
          metric('completed_project_count', 'company.roi.completedProjects', FORMAT.INTEGER, BETTER.HIGHER),
          metric('allocated_days', 'roiCompare.allocatedDays', FORMAT.INTEGER, BETTER.HIGHER),
        ] },
        { labelKey: 'roiCompare.group.economics', metrics: [
          metric('revenue', 'common.revenue', FORMAT.MONEY, BETTER.HIGHER, { labelCount: 2 }),
          metric('cost', 'common.cost', FORMAT.MONEY),
          metric('margin', 'common.margin', FORMAT.MONEY, BETTER.HIGHER, { signed: true }),
          metric('revenue_per_day', 'roiCompare.revenuePerDay', FORMAT.MONEY, BETTER.HIGHER),
        ] },
        { labelKey: 'roiCompare.group.performance', metrics: [
          metric('margin_pct', 'roiCompare.marginPct', FORMAT.PERCENT, BETTER.HIGHER, { signed: true }),
        ] },
      ],
    },
  }

  definitionsCache = { definitions, TYPE, FORMAT, BETTER }
  return definitionsCache
}

export const useRoiComparisonFormat = () => {
  const { t } = getI18n()
  const planningStore = usePlanningStore()
  const webSiteStore = useWebSiteStore()
  const { getCurrencyString } = useCurrency()
  const { isGreater, isLess } = useNumberComparison()
  const { roiBarToneEnum } = planningStore
  const { definitions, TYPE, FORMAT, BETTER } = useRoiComparisonDefinitions()

  const toNumber = (value) => (isNotEmpty(value) ? Number(value) : null)

  const formatNumber = (value, options = {}) => {
    const key = `${getI18n().locale.value}|${JSON.stringify(options)}`
    if (!numberFormatCache.has(key)) numberFormatCache.set(key, new Intl.NumberFormat(getI18n().locale.value, options))
    return numberFormatCache.get(key).format(value)
  }

  const formatters = {
    [FORMAT.MONEY]: (value) => {
      const code = webSiteStore.getDefaultCurrency()?.code
      return code ? getCurrencyString(value, code) : formatNumber(value)
    },
    [FORMAT.PERCENT]: (value) => `${formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`,
    [FORMAT.NUMBER]: (value) => formatNumber(value, { maximumFractionDigits: 1 }),
    [FORMAT.INTEGER]: (value) => formatNumber(value, { maximumFractionDigits: 0 }),
    [FORMAT.DATE]: (value) => formatShortDate(value),
  }

  const metricLabel = (metric) => t(metric.labelKey, metric.labelCount ?? 1)
  const groupLabel = (group) => t(group.labelKey, group.labelCount ?? 1)
  const typeLabel = (type) => t(definitions[type].labelKey, 2)

  const metricValue = (metric, values) => (metric.format === FORMAT.DATE ? values?.[metric.key] ?? null : toNumber(values?.[metric.key]))

  const formatMetric = (metric, values) => {
    if (metric.display) return metric.display(values) || '—'
    const value = metricValue(metric, values)
    return value === null ? '—' : formatters[metric.format](value)
  }

  const signedClass = (metric, values) => {
    if (!metric.signed) return null
    const value = metricValue(metric, values)
    if (isGreater(value, 0)) return 'text-positive text-bold'
    if (isLess(value, 0)) return 'text-negative text-bold'
    return null
  }

  const isNumeric = (metric) => metric.format !== FORMAT.DATE

  const numericValues = (metric, rows) => rows.map(row => metricValue(metric, row.values)).filter(value => value !== null)

  const bestIds = (metric, rows) => {
    const values = numericValues(metric, rows)
    if (metric.better === BETTER.NONE || values.length < ROI_COMPARISON_LIMITS.MIN) return new Set()
    const target = metric.better === BETTER.HIGHER ? Math.max(...values) : Math.min(...values)
    if (values.every(value => value === target)) return new Set()
    return new Set(rows.filter(row => metricValue(metric, row.values) === target).map(row => row.id))
  }

  const isUniform = (metric, rows) => new Set(rows.map(row => String(metricValue(metric, row.values)))).size <= 1

  const barWidth = (metric, values, rows) => {
    const value = metricValue(metric, values)
    if (!isNumeric(metric) || value === null) return 0
    const max = Math.max(...numericValues(metric, rows).map(Math.abs))
    return max ? Math.abs(value) / max * 100 : 0
  }

  const deltaPercent = (metric, values, referenceValues) => {
    const value = metricValue(metric, values)
    const reference = metricValue(metric, referenceValues)
    if (!isNumeric(metric) || value === null || !reference) return null
    return (value - reference) / Math.abs(reference) * 100
  }

  const deltaClass = (metric, delta) => {
    if (!delta || metric.better === BETTER.NONE) return 'text-grey-7'
    return (metric.better === BETTER.HIGHER) === (delta > 0) ? 'text-positive' : 'text-negative'
  }

  const formatDelta = (delta) => `${delta > 0 ? '+' : ''}${formatters[FORMAT.NUMBER](delta)}%`

  const referenceRow = (comparison) => comparison.rows.find(row => row.id === comparison.referenceId)

  const visibleMetrics = (metrics, rows) => metrics.filter(metric => !webSiteStore.roiComparisonDifferencesOnly || !isUniform(metric, rows))

  const cellDelta = (comparison, metric, row) => {
    const reference = referenceRow(comparison)
    if (!webSiteStore.roiComparisonShowDelta || row.id === comparison.referenceId || !reference) return null
    return deltaPercent(metric, row.values, reference.values)
  }

  const barTone = (metric, row, bestSet) => {
    if (isLess(metricValue(metric, row.values), 0)) return roiBarToneEnum.NEGATIVE
    return bestSet.has(row.id) ? roiBarToneEnum.BEST : roiBarToneEnum.MUTED
  }

  const valueClass = (metric, row, bestSet) => (bestSet.has(row.id) ? 'text-positive text-bold' : signedClass(metric, row.values))

  const itemName = (type, item) => (type === TYPE.COMPANY ? item.name : planningStore.getServiceName(item, false, true))
  const itemRoute = (type, item) => ({ path: menuRoutes[definitions[type].menuRoute], query: { code: item.code } })
  const itemAvatar = (item) => item.avatar_file || item.avatar_url
  const itemInitials = (type, item) => getInitials(type === TYPE.STAFF ? [item.first_name, item.name].join(' ') : item.name)
  const comparisonCaption = (layout) => [
    typeLabel(layout.data.type),
    `${layout.data.ids.length} ${t('roiCompare.item', layout.data.ids.length).toLowerCase()}`,
    webSiteStore.isOwnRecord(layout) ? t('roiCompare.createdByYou') : layout.created_by_name,
  ].join(' · ')

  return {
    definitions,
    TYPE,
    metricLabel,
    groupLabel,
    typeLabel,
    formatMetric,
    isNumeric,
    bestIds,
    visibleMetrics,
    barWidth,
    deltaClass,
    formatDelta,
    referenceRow,
    cellDelta,
    barTone,
    valueClass,
    itemName,
    itemRoute,
    itemAvatar,
    itemInitials,
    comparisonCaption,
  }
}

export const useRoiComparisonPersistence = () => {
  const planningStore = usePlanningStore()
  const webSiteStore = useWebSiteStore()
  const { itemName } = useRoiComparisonFormat()

  const saveAsync = async({ id, name, isShared, type, items, referenceId }) => {
    const payload = {
      name,
      data: { type, ids: items.map(item => item.id), names: items.map(item => itemName(type, item)), reference_id: referenceId },
      ...(webSiteStore.can(webSiteStore.permissionFlagEnum.COMPANY_SETTINGS) && { is_shared: isShared }),
    }
    const { data } = id
      ? await webSiteStore.updateElementLayoutAsync(id, payload)
      : await webSiteStore.createElementLayoutAsync({ ...payload, element_type: elementLayoutTypeEnum.ROI_COMPARISON, element_key: '' })
    return data
  }

  const fetchItemsAsync = async(type, ids) => {
    const { data } = await planningStore.fetchRoiComparisonItemsAsync(type, ids)
    const byId = Object.fromEntries(data.map(item => [item.id, item]))
    return ids.map(id => byId[id]).filter(Boolean)
  }

  return { saveAsync, fetchItemsAsync }
}

export const useRoiComparison = () => {
  const planningStore = usePlanningStore()
  const webSiteStore = useWebSiteStore()
  const { TYPE } = useRoiComparisonFormat()
  const { fetchItemsAsync } = useRoiComparisonPersistence()

  const current = ref(null)
  const items = ref([])
  const stats = ref({})
  const loading = ref(false)
  const savedComparisons = ref([])
  const savedItems = ref({})

  const type = computed(() => current.value?.data.type ?? TYPE.EQUIPMENT)
  const rows = computed(() => items.value.map(item => ({ id: item.id, item, values: { ...item, ...stats.value[item.id] } })))
  const referenceId = computed(() => resolveReferenceId(items.value, current.value?.data.reference_id))

  const visibleComparisons = computed(() => savedComparisons.value
    .filter(layout => (webSiteStore.roiComparisonShowMine && webSiteStore.isOwnRecord(layout)) || (webSiteStore.roiComparisonShowShared && layout.is_shared)))

  const fetchSavedAsync = async() => {
    const layouts = await webSiteStore.fetchElementLayoutsAsync(elementLayoutTypeEnum.ROI_COMPARISON)
    await fetchSavedItemsAsync(layouts)
    savedComparisons.value = layouts.sort((a, b) => b.updated_at.localeCompare(a.updated_at))
  }

  const fetchSavedItemsAsync = async(layouts) => {
    const types = [...new Set(layouts.map(layout => layout.data.type))]
    const idsOf = (type) => [...new Set(layouts.filter(layout => layout.data.type === type).flatMap(layout => layout.data.ids))]
    const itemLists = await Promise.all(types.map(type => fetchItemsAsync(type, idsOf(type))))
    savedItems.value = Object.fromEntries(types.map((type, index) => [type, Object.fromEntries(itemLists[index].map(item => [item.id, item]))]))
  }

  const layoutItems = (layout) => layout.data.ids.map(id => savedItems.value[layout.data.type]?.[id]).filter(Boolean)

  const openAsync = async(layout, knownItems = null) => {
    loading.value = true
    const { type: layoutType, ids } = layout.data
    const [loadedItems, { data: statRows }] = await Promise.all([
      knownItems ?? fetchItemsAsync(layoutType, ids),
      planningStore.fetchRoiComparisonAsync(layoutType, ids),
    ])
    items.value = loadedItems
    stats.value = Object.fromEntries(statRows.map(row => [row.id, row]))
    current.value = layout
    loading.value = false
  }

  const close = () => {
    current.value = null
    items.value = []
    stats.value = {}
  }

  const deleteSavedAsync = async(layout) => {
    await webSiteStore.deleteElementLayoutAsync(layout.id)
    if (current.value?.id === layout.id) close()
    await fetchSavedAsync()
  }

  return reactive({
    current,
    type,
    items,
    rows,
    loading,
    referenceId,
    savedComparisons,
    visibleComparisons,
    layoutItems,
    fetchSavedAsync,
    openAsync,
    close,
    deleteSavedAsync,
  })
}

export const useRoiCompareBasket = (type, gridRef) => {
  const i18n = getI18n()
  const planningStore = usePlanningStore()
  const webSiteStore = useWebSiteStore()
  const { notifyWarning } = useNotify()

  const selectedRows = ref([])
  let addButton = null

  const items = computed(() => planningStore.roiCompareBasket[type])
  const { isFull, isComparable, limitColor, limitLabel } = useRoiComparisonLimits(items)

  const refreshAddButton = () => addButton?.option('disabled', !selectedRows.value.length || isFull.value)

  const setItems = (value) => {
    const changedIds = [...items.value, ...value].map(item => item.id).filter(id => items.value.some(item => item.id === id) !== value.some(item => item.id === id))
    planningStore.roiCompareBasket[type] = value
    refreshAddButton()
    const grid = gridRef.value?.instance
    grid?.repaintRows(changedIds.map(id => grid.getRowIndexByKey(id)).filter(index => index >= 0))
  }

  const add = () => {
    const ids = new Set(items.value.map(item => item.id))
    const candidates = selectedRows.value.filter(row => !row.archived && !ids.has(row.id))
    const free = ROI_COMPARISON_LIMITS.MAX - items.value.length
    if (candidates.length > free) notifyWarning(i18n.t('roiCompare.maxReached', { max: ROI_COMPARISON_LIMITS.MAX }))
    gridRef.value.instance.clearSelection()
    setItems([...items.value, ...candidates.slice(0, free)])
  }

  const remove = (id) => setItems(items.value.filter(item => item.id !== id))
  const clear = () => setItems([])

  const onSelectionChanged = (e) => {
    selectedRows.value = e.selectedRowsData
    refreshAddButton()
  }

  const onRowPrepared = (e) => {
    if (e.rowType === 'data' && items.value.some(item => item.id === e.data.id)) e.rowElement.classList.add('roi-basket-row')
  }

  const toolbarItem = markRaw({
    widget: 'dxButton',
    location: 'after',
    visible: webSiteStore.can(webSiteStore.permissionFlagEnum.FINANCIALS),
    options: {
      icon: 'mdi mdi-chart-line',
      text: i18n.t('roiCompare.addToComparison'),
      disabled: true,
      onClick: add,
      onInitialized: (e) => { addButton = e.component },
    },
  })

  return reactive({ type, items, isComparable, limitColor, limitLabel, toolbarItem, remove, clear, onSelectionChanged, onRowPrepared })
}
